import React, { useCallback, useState, useMemo } from 'react';
import styled from 'styled-components';
import TrueImpactText, {
  textSelectionNone,
} from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import ContributionMappingTableRow from './ContributionMappingTableRow';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import { parsingRows } from './worker';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { getColumnMapping } from '../ContributionMapping/worker';
import {
  InvestmentColumnKeys,
  downloadDataUrl,
  investmentSaveMapping,
} from '../utils';
import TrueImpactToggle from 'shared/design-system/components/TrueImpactToggle';
import { faDownload } from '@fortawesome/pro-light-svg-icons';
import TrueImpactIconButton from 'shared/design-system/components/TrueImpactIconButton';
import PaginationPageInfo from './PaginationPageInfo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { useClient } from 'shared/apollo';
import { SuccessResponse } from 'npo/pages/ReportsBeta/NewReport/api';
import { bulkInvestmentUpload } from '../api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleXmark } from '@fortawesome/pro-light-svg-icons';
import TrueImpactModal, {
  modalHeightAutoStyles,
} from 'shared/design-system/components/TrueImpactModal';
import { requestInit } from 'shared/auth/hooks/useRequestState';

const refreshPage = () => {
  window.location.reload();
};

interface ContributionMappingTableProps {
  data: string[][] | undefined;
  isLoadingValidation: boolean;
  columns: ReturnType<typeof getColumnMapping> | undefined;
  finalData: ReturnType<typeof parsingRows> | undefined;
  moveToFileStep: () => unknown;
  moveToMappingStep: () => unknown;
  moveToTableStep: () => unknown;
  handleDeleteRow: (rowIndex: number) => unknown;
  handleUpdateRow: (
    rowIndex: number,
    columnIndex: number,
    systemKey: InvestmentColumnKeys,
    value: string,
  ) => unknown;
}

const StyledDiv = styled.div`
  .mapping-content-heading {
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
    .mapping-heading {
      margin-bottom: ${SPACERS.small};
    }
    .mapping-subheading {
      margin-bottom: ${SPACERS.large};
    }
    .mapping-button-wrapper {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: ${SPACERS.small};
      margin-bottom: ${SPACERS.medium};
    }
    .functionality-buttons {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: ${SPACERS.small};
      .show-all-toggle-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        .show-all-toggle {
          margin-left: ${SPACERS.tiny};
        }
      }
      .download-icon {
        margin-left: ${SPACERS.tiny};
      }  
    }
  }
  .table-header {
    &,
    .MuiTableRow-head,
    .MuiTableCell-stickyHeader {
      background-color: ${COLORS.grayLight};
    }
    * {
      font-weight: bold;
    }
    .table-header-cell {
      border-top: 1px solid ${COLORS.grayBase};
    }
  }
  .table-pagination {
    display: flex;
    justify-content: space-between;
    ${textSelectionNone}
    background-color: ${COLORS.grayLight};
    .MuiToolbar-root {
      padding-right: ${SPACERS.small};
    }
    .MuiTableCell-root {
      border: none;
    }
    .MuiInputBase-inputMarginDense {
      padding-top: 7px;
    }
  }
  .MuiTableCell-root {
    white-space: nowrap;
    border-right: 1px solid ${COLORS.grayBase};
    border-bottom: 1px solid ${COLORS.grayBase};
  }
  .success, .error {
    button {
      margin-left: auto;
      margin-right: auto;
    }
    .refresh-button {
      margin-bottom: ${SPACERS.extraLarge};
    }
    .mapping-heading {
      margin-top: ${SPACERS.small};
      margin-bottom: ${SPACERS.medium};
    }
    svg {
      width: 50px;
      height: 50px;
    }
  }
  .error {
    padding-bottom: ${SPACERS.small};
  }
`;

const defaultRowsPerPage = [5, 10, 25, 100, 200];
type onChangeTableEvent = React.ChangeEvent<
  HTMLTextAreaElement | HTMLInputElement
>;
const ContributionMappingTable: React.FC<ContributionMappingTableProps> = ({
  data,
  columns,
  finalData,
  isLoadingValidation,
  moveToFileStep,
  handleUpdateRow,
  moveToMappingStep,
  handleDeleteRow,
}) => {
  const client = useClient();
  const [
    bulkInvestmentRequestState,
    makeBulkInvestmentRequest,
    setBulkInvestmentRequest,
  ] = useGraphQLRequest<SuccessResponse<'bulkInsertInvestments'>>();
  const [page, setPage] = useState<number>(0);
  const [showAll, setShowAll] = useState<boolean>(true);
  const [rowsPerPage, setRowsPerPage] = useState<number>(defaultRowsPerPage[0]);
  const [sendInvitation, setSendInvitation] = useState(false);

  const handleShowAll = useCallback(() => {
    setPage(0);
    setShowAll(s => !s);
  }, [setShowAll, setPage]);

  const handleSubmit = useCallback(() => {
    if (!finalData || !client) return;
    makeBulkInvestmentRequest(
      bulkInvestmentUpload(
        client,
        investmentSaveMapping(finalData.result),
        sendInvitation,
      ),
    );
  }, [finalData, client, sendInvitation, makeBulkInvestmentRequest]);

  const handleDownload = useCallback(() => {
    if (!data) return;
    downloadDataUrl(data);
  }, [data]);

  const handleChangePage = useCallback(
    (_e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      setPage(newPage);
    },
    [setPage],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: onChangeTableEvent) => {
      setRowsPerPage(+event.target.value);
      setPage(0);
    },
    [setPage, setRowsPerPage],
  );

  const refinedData = useMemo(() => {
    if (showAll || !data || !finalData) return data;
    const result: string[][] = [data[0]];
    for (let i = 1; i < data.length; i++) {
      const d = data[i];
      const validation = finalData.failedRowsIndexes[i];
      if (validation === undefined) {
        continue;
      } else if (validation === true) {
        result.push(d);
      } else {
        Object.keys(validation).length > 0 && result.push(d);
      }
    }
    return result;
  }, [data, showAll, finalData]);

  if (!finalData || !data || !refinedData || !columns) {
    return <TrueImpactLoading />;
  }

  const startingIndex = page * rowsPerPage;
  const endingIndex = page * rowsPerPage + rowsPerPage;
  const isValid =
    Object.keys(finalData.failedRowsIndexes).length === 0 &&
    Object.keys(finalData.result).length > 0;
  const rows = isValid || showAll ? data : refinedData;

  if (
    bulkInvestmentRequestState.type === 'REQUEST_SUCCESS' &&
    bulkInvestmentRequestState.data.bulkInsertInvestments.success
  ) {
    return (
      <StyledDiv>
        <div className="mapping-content-heading success">
          <FontAwesomeIcon icon={faCircleCheck} color={COLORS.success} />
          <TrueImpactText variant="heading-page" className="mapping-heading">
            Successfully upload all the investments
          </TrueImpactText>
          <TrueImpactButton
            variant="secondary-large"
            className="refresh-button"
            onClick={refreshPage}
          >
            Upload another file
          </TrueImpactButton>
        </div>
      </StyledDiv>
    );
  }

  if (
    bulkInvestmentRequestState.type === 'REQUEST_ERROR' ||
    bulkInvestmentRequestState.type === 'REQUEST_SUCCESS'
  ) {
    // Extract error message from the response
    let errorMessage = 'Unable to save investments';
    if (bulkInvestmentRequestState.type === 'REQUEST_ERROR') {
      errorMessage = bulkInvestmentRequestState.error?.message || errorMessage;
    } else if (
      bulkInvestmentRequestState.type === 'REQUEST_SUCCESS' &&
      bulkInvestmentRequestState.data?.bulkInsertInvestments?.error
    ) {
      errorMessage = bulkInvestmentRequestState.data.bulkInsertInvestments.error;
    }
    
    return (
      <TrueImpactModal
        open={true}
        hideFooter={true}
        addStyles={modalHeightAutoStyles}
        onClose={() => setBulkInvestmentRequest(requestInit())}
      >
        <StyledDiv>
          <div className="mapping-content-heading error">
            <FontAwesomeIcon icon={faCircleXmark} color={COLORS.critical} />
            <TrueImpactText variant="heading-page" className="mapping-heading">
              Unable to save investments
            </TrueImpactText>
            <TrueImpactText variant="body" style={{ marginTop: '8px' }}>
              {errorMessage}
            </TrueImpactText>
          </div>
        </StyledDiv>
      </TrueImpactModal>
    );
  }

  return (
    <StyledDiv>
      <div className="mapping-content-heading">
        <TrueImpactText variant="heading-page" className="mapping-heading">
          Here’s how your data will be imported.
        </TrueImpactText>
        <TrueImpactText className="mapping-subheading">
          We’ll create a donation for each row below and place it in it’s
          designated portfolio.
        </TrueImpactText>
        <div className="mapping-button-wrapper">
          <TrueImpactButton
            variant="secondary-large"
            onClick={moveToMappingStep}
          >
            Make changes to field mapping
          </TrueImpactButton>
          <TrueImpactButton variant="secondary-large" onClick={moveToFileStep}>
            Upload different file
          </TrueImpactButton>
          <TrueImpactButton
            disabled={!isValid}
            isLoading={bulkInvestmentRequestState.type === 'REQUEST_START'}
            onClick={handleSubmit}
          >
            Upload these donations
          </TrueImpactButton>
        </div>
        <div className="functionality-buttons">
          <div className="show-all-toggle-wrapper">
            <TrueImpactText variant="body-small">Show All Rows:</TrueImpactText>
            <TrueImpactToggle
              enabled={isValid || showAll}
              disabled={isValid}
              onClick={handleShowAll}
              className="show-all-toggle"
            />
          </div>
          <div className="show-all-toggle-wrapper">
            <TrueImpactText variant="body-small">
              Send invitation:
            </TrueImpactText>
            <input
              type="checkbox"
              style={{ marginLeft: 8 }}
              checked={sendInvitation}
              onChange={() => setSendInvitation(prev => !prev)}
            />
          </div>
          <TrueImpactIconButton
            text="Download"
            label="Download the changes"
            color="ctaPrimary"
            className="download-icon"
            icon={faDownload}
            onClick={handleDownload}
          />
        </div>
      </div>
      <TableContainer>
        <Table stickyHeader>
          <TableHead className="table-header">
            <TableRow>
              {rows[0].map(r => (
                <TableCell key={r} className="table-header-cell">
                  {r}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows
              .slice(1)
              .slice(startingIndex, endingIndex)
              .map((x, i) => (
                <ContributionMappingTableRow
                  key={i}
                  row={x}
                  columns={columns}
                  finalData={finalData}
                  index={Number(x[0])}
                  isLoadingValidation={isLoadingValidation}
                  handleDeleteRow={handleDeleteRow}
                  handleUpdateRow={handleUpdateRow}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <div className="table-pagination">
        <PaginationPageInfo
          currentPage={page + 1}
          totalPages={Math.ceil((rows.length - 1) / rowsPerPage)}
          setPage={setPage}
        />
        <TablePagination
          rowsPerPageOptions={defaultRowsPerPage}
          count={rows.length - 1}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </div>
    </StyledDiv>
  );
};

export default ContributionMappingTable;
